import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Microscope,
  ShieldCheck,
  Snowflake,
  Package,
  HelpCircle,
  Beaker,
  Droplets,
} from "lucide-react";
import { getProductForm, getProductMeta } from "../lib/productMeta";

/**
 * Long-form product-detail content. Form-aware: routes to a template
 * appropriate for the physical form of the product (sterile solution,
 * alcohol prep pad, small molecule, protein, peptide, or blend) so we
 * NEVER describe a liquid as a "lyophilized powder" and vice versa.
 *
 * All templates are compliance-safe: chemistry, handling, QA, shipping —
 * never dosing, reconstitution instructions, or administration guidance.
 */
export default function ProductLongContent({ product }) {
  const form = getProductForm(product);
  const meta = getProductMeta(product.slug);

  const sections = buildSections(product, meta, form);

  return (
    <section className="mt-20 border-t border-white/10 pt-16" data-testid="product-long-content" data-form={form}>
      <div className="overline">DETAILED PRODUCT INFORMATION</div>
      <h2 className="font-heading text-3xl sm:text-4xl mt-3 mb-2 font-light">
        {form === "cosmetic"
          ? `Everything you need to know about ${product.name}`
          : `Everything researchers need to know about ${product.name}`}
      </h2>
      <p className="text-sm text-[#8A94A6] max-w-2xl mb-14">
        {form === "cosmetic"
          ? `Ingredients, directions, and product details for ${product.name}. Manufactured in the USA by ProSource Labs, Corona, California.`
          : `Manufacturing, testing, handling, and shipping details for ${product.name}. For research purposes only — not for human or animal consumption.`}
      </p>

      <div className="space-y-14 max-w-3xl">
        {sections.map((s, i) => (
          <div key={i} data-testid={`product-section-${i}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[#00C3FF]">{s.icon}</span>
              <h3 className="font-heading text-xl text-white">{s.title}</h3>
            </div>
            <div className="text-[#B8C0CE] leading-relaxed space-y-4 text-[15px]">
              {s.body}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between max-w-3xl">
        <div className="flex items-start gap-3">
          <HelpCircle className="text-[#00C3FF] shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-heading text-base text-white">
              Still have questions about {product.name}?
            </div>
            <p className="text-sm text-[#8A94A6] mt-1">
              Our full FAQ covers ordering, wholesale, lab-verification, and
              customer support topics.
            </p>
          </div>
        </div>
        <Link
          to="/faqs"
          className="shrink-0 self-start sm:self-auto px-4 py-2 border border-[#00C3FF]/40 text-[#00C3FF] hover:bg-[#00C3FF]/10 transition-colors font-mono-tech text-[10px] uppercase tracking-[0.18em]"
          data-testid="product-faq-link"
        >
          View All FAQs →
        </Link>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------- */
/*  Template builders                                                    */
/* -------------------------------------------------------------------- */

/**
 * Render the product-specific overview prose from metadata when available,
 * falling back to the template's default wording otherwise. This is what
 * makes each product page truly individualized instead of blanket-copied.
 */
function CustomOverview({ meta, fallback }) {
  if (meta?.overview) return <p>{meta.overview}</p>;
  return fallback;
}

function CustomApplication({ meta, fallback }) {
  if (meta?.researchApplication) return <p>{meta.researchApplication}</p>;
  return fallback;
}

/**
 * Render a keyFacts entry. When the fact string is written as
 * "Bold Label: description text", split on the FIRST colon and bold the
 * label so consumer-facing benefit lists (e.g. "Localized Healing: Targets
 * tendons, ligaments…") match the person-to-person visual pattern the
 * store owner wrote. Falls back to a plain string when there's no colon.
 */
function KeyFactItem({ text }) {
  const idx = text.indexOf(":");
  if (idx > 0 && idx < 40) {
    const label = text.slice(0, idx).trim();
    const rest = text.slice(idx + 1).trim();
    return (
      <li>
        <strong className="text-white">{label}:</strong> {rest}
      </li>
    );
  }
  return <li>{text}</li>;
}

function StorageProtocol({ name }) {
  return (
    <div className="space-y-3" data-testid="storage-protocol">
      <p><strong>Lyophilized {name} (powder form):</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Short term (weeks to 12 months): store at <strong>2–8°C (36–46°F)</strong> in a refrigerator.</li>
        <li>Long term (12+ months): store at <strong>-20°C (-4°F)</strong> in a freezer. Freezer storage is only necessary when 12+ month storage without reconstitution is planned.</li>
        <li>Keep vials tightly sealed and protected from moisture. Plastic storage containers with a rubber gasket and desiccant packs are highly recommended.</li>
      </ul>
      <p><strong>Reconstituted {name} (in solution):</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li>In use (up to 28 days): store at <strong>2–8°C (36–46°F)</strong> in a refrigerator.</li>
        <li><strong>Never store reconstituted peptides in a freezer.</strong> Never leave reconstituted peptides at room temperature.</li>
      </ul>
    </div>
  );
}

function buildSections(product, meta, form) {
  const name = product.name;
  const variants = product.variants || [];
  const sizes = variants.map((v) => v.size).filter(Boolean).join(", ");
  const skus = variants.map((v) => v.sku).filter(Boolean).join(", ");

  const ctx = { product, meta, name, sizes, skus };

  switch (form) {
    case "solution":
      return solutionTemplate(ctx);
    case "pad":
      return padTemplate(ctx);
    case "small-molecule":
      return smallMoleculeTemplate(ctx);
    case "coenzyme":
      return coenzymeTemplate(ctx);
    case "protein":
      return proteinTemplate(ctx);
    case "blend":
      return blendTemplate(ctx);
    case "cosmetic":
      return cosmeticTemplate(ctx);
    case "nasal-spray":
      return nasalSprayTemplate(ctx);
    case "peptide":
    default:
      return peptideTemplate(ctx);
  }
}

/* ---------- SOLUTION (Bacteriostatic Water, Acetic Acid) ---------- */
function solutionTemplate({ product, meta, name, sizes, skus }) {
  const fullName = meta?.fullName || name;
  const composition = meta?.composition || "Sterile aqueous laboratory solution";
  const physicalForm = meta?.physicalForm || "Clear aqueous solution";
  const primaryUse =
    meta?.primaryUse || "Standard laboratory reagent used in dilution and dissolution workflows";
  const storage = meta?.storage || "Store at room temperature (15–30°C), tightly capped.";

  return [
    {
      icon: <Beaker size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{fullName}</strong> is a laboratory-grade sterile aqueous
                solution supplied by ProSource Labs from our Southern California
                facility. It is a <strong>liquid product — not a lyophilized peptide
                powder</strong> — and is classified in the {product.category || "Lab Supplies"} category
                of our catalog because it functions as a laboratory reagent rather than
                a research active compound.
              </p>
              <p>
                <strong>Composition:</strong> {composition}. <strong>Physical form:</strong> {physicalForm}. Every vial ships with a batch identifier
                so researchers can trace the product back to its manufacturing lot and
                associated quality documentation.
              </p>
              <p>
                {fullName} is offered <strong>strictly for laboratory research
                applications</strong>. It is not intended for and is not approved for
                human or veterinary consumption. All orders ship from Southern
                California with tracked domestic delivery.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Droplets size={16} />,
      title: `${name} — Formulation & Vial Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in the following available vial sizes
            for research purchase: <strong>{sizes || "—"}</strong>. Product SKU
            references: <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          {meta?.keyFacts && meta.keyFacts.length > 0 && (
            <ul className="list-disc pl-6 space-y-1">
              {meta.keyFacts.map((f, i) => (
                <KeyFactItem key={i} text={f} />
              ))}
            </ul>
          )}
          <p>
            Each vial is <strong>sealed under sterile conditions</strong> in the
            source manufacturer's cGMP facility (for pharmaceutical-grade
            solutions like Hospira Bacteriostatic Water) or in a validated
            laboratory environment for laboratory-grade reagents. Vials are
            visually inspected for particulate matter and seal integrity before
            release.
          </p>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Handling & Storage Guidelines`,
      body: (
        <>
          <p>
            <strong>Storage: {storage}</strong>
          </p>
          <p>
            Because {name} is a liquid, freezing is not appropriate — freeze-thaw
            cycles can compromise the vial's seal, alter the concentration through
            water-ice expansion damage, and (for preserved solutions) can affect
            preservative distribution. Store the sealed vial upright.
          </p>
          <p>
            All laboratory handling should follow the receiving institution's
            standard operating procedures for sterile reagents. Once a vial's
            rubber septum has been punctured, the contents should be treated
            per the corresponding manufacturer's product monograph.
          </p>
        </>
      ),
    },
    {
      icon: <ShieldCheck size={16} />,
      title: `${name} — Quality & Documentation`,
      body: (
        <>
          <p>
            Because {name} is a <strong>liquid reagent rather than a synthetic
            peptide</strong>, HPLC purity testing is not the relevant analytical
            method. Quality documentation for this product covers the
            manufacturer's <strong>certificate of sterility, endotoxin testing,
            fill-volume verification</strong>, and (for preserved products)
            preservative concentration analysis.
          </p>
          <p>
            For pharmaceutical-grade products supplied through ProSource Labs
            (such as Hospira Bacteriostatic Water for Injection), the underlying
            manufacturer's <strong>FDA-approved product monograph</strong> is the
            authoritative reference for composition, storage, and in-use
            timelines. Request the source documentation from customer support
            after purchase if required for your institution's incoming QA
            workflow.
          </p>
        </>
      ),
    },
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Typical Laboratory Application`,
      body: (
        <CustomApplication
          meta={meta}
          fallback={
            <>
              <p>{primaryUse}.</p>
              <p>
                Researchers should consult the peer-reviewed protocol relevant to
                their specific experimental question when selecting a diluent or
                reagent. ProSource Labs, as a research supplier, does not publish
                end-user preparation protocols — every institution's biosafety and
                chemical hygiene framework differs.
              </p>
            </>
          }
        />
      ),
    },
    shippingSection(name),
  ];
}

/* ---------- PAD (Alcohol Prep Pads) ---------- */
function padTemplate({ product, meta, name, sizes, skus }) {
  const composition = meta?.composition || "70% Isopropyl Alcohol on lint-free non-woven pad";
  const primaryUse = meta?.primaryUse || "Laboratory surface and vial-septum disinfection";
  const storage = meta?.storage || "Room temperature; keep pouches sealed until use.";

  return [
    {
      icon: <Package size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{name}</strong> are <strong>pre-saturated sterile
                single-use wipes</strong> — not a peptide, powder, or liquid
                reagent. Each pad is <strong>individually foil-pouched</strong> to
                preserve sterility until point-of-use. Classified under {product.category || "Lab Supplies"}.
              </p>
              <p>
                <strong>Composition:</strong> {composition}. Every pouch bears a lot
                code so researchers can trace product back to its manufacturing
                batch.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in the following pack sizes:{" "}
            <strong>{sizes || "—"}</strong>. Product SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          {meta?.keyFacts && meta.keyFacts.length > 0 && (
            <ul className="list-disc pl-6 space-y-1">
              {meta.keyFacts.map((f, i) => (
                <KeyFactItem key={i} text={f} />
              ))}
            </ul>
          )}
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Handling & Storage`,
      body: (
        <>
          <p>
            <strong>Storage:</strong> {storage} Store away from open flame — 70%
            isopropyl alcohol is flammable.
          </p>
          <p>
            Discard the pad after a single use. Do not re-seal opened pouches —
            evaporation and airborne contamination compromise both the alcohol
            concentration and sterility.
          </p>
        </>
      ),
    },
    {
      icon: <ShieldCheck size={16} />,
      title: `${name} — Quality`,
      body: (
        <>
          <p>
            Because {name} are <strong>sterile wipes rather than a synthetic
            peptide</strong>, HPLC purity testing is not the relevant analytical
            method. Quality documentation covers the manufacturer's{" "}
            <strong>sterility validation and alcohol-concentration verification</strong>{" "}
            for each production lot.
          </p>
        </>
      ),
    },
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Laboratory Application`,
      body: <CustomApplication meta={meta} fallback={<p>{primaryUse}.</p>} />,
    },
    shippingSection(name),
  ];
}

/* ---------- SMALL MOLECULE (AICAR, SLU-PP-332) ---------- */
function smallMoleculeTemplate({ product, meta, name, sizes, skus }) {
  const fullName = meta?.fullName || name;
  const chemicalClass = meta?.chemicalClass || "Small-molecule research compound";
  const mw = meta?.molecularWeight;
  const formula = meta?.molecularFormula;
  const primaryUse = meta?.primaryUse || "Preclinical research applications";

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{fullName}</strong> is a <strong>{chemicalClass}</strong>{" "}
                supplied by ProSource Labs for laboratory research. Please note that{" "}
                {name} is <em>not</em> a synthetic peptide — it is a small-molecule
                research compound in the {product.category || "research"} category.
              </p>
              <p>
                {name} is offered <strong>strictly for laboratory research
                applications</strong> — including cell-culture assays, receptor and
                pathway characterization studies, and preclinical mechanism-of-action
                work in animal models. Not intended for and not approved for human or
                veterinary consumption.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Chemical Characteristics & Vial Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in the following available vial sizes:{" "}
            <strong>{sizes || "—"}</strong>. Product SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Chemical class:</strong> {chemicalClass}</li>
            {formula && <li><strong>Molecular formula:</strong> {formula}</li>}
            {mw && <li><strong>Molecular weight:</strong> {mw}</li>}
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Laboratory Handling & Storage`,
      body: (
        <>
          <StorageProtocol name={name} />
          <p>
            Small-molecule compounds are often hygroscopic — do not open the
            vial in a humid environment.
          </p>
          <p>
            All laboratory handling of {name} should follow the receiving
            institution's SOPs for research chemicals and any applicable
            peer-reviewed protocols. ProSource Labs does not publish end-user
            preparation or usage instructions.
          </p>
        </>
      ),
    },
    analyticalSection(name, "small-molecule"),
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: <CustomApplication meta={meta} fallback={<p>{primaryUse}.</p>} />,
    },
    shippingSection(name),
  ];
}

/* ---------- COENZYME (NAD+) ---------- */
function coenzymeTemplate({ product, meta, name, sizes, skus }) {
  const fullName = meta?.fullName || name;
  const chemicalClass = meta?.chemicalClass || "Cellular coenzyme";
  const mw = meta?.molecularWeight;
  const formula = meta?.molecularFormula;
  const primaryUse = meta?.primaryUse || "Preclinical research on cellular metabolism";

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{fullName}</strong> is a{" "}
                <strong>{chemicalClass}</strong> supplied by ProSource Labs for
                laboratory research. {name} is a small biochemical cofactor — <em>not
                a synthetic peptide</em> — and is catalogued in the{" "}
                {product.category || "Longevity Research"} category.
              </p>
              <p>
                {name} ships as a lyophilized powder and is offered strictly for
                laboratory research applications. Not intended for and not approved
                for human or veterinary consumption.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Chemical Characteristics & Vial Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in the following available vial sizes:{" "}
            <strong>{sizes || "—"}</strong>. Product SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Chemical class:</strong> {chemicalClass}</li>
            {formula && <li><strong>Molecular formula:</strong> {formula}</li>}
            {mw && <li><strong>Molecular weight:</strong> {mw}</li>}
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Laboratory Handling & Storage`,
      body: (
        <>
          <StorageProtocol name={name} />
          <p>
            NAD+ is hygroscopic — do not open the vial in a humid environment.
          </p>
        </>
      ),
    },
    analyticalSection(name, "coenzyme"),
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: <CustomApplication meta={meta} fallback={<p>{primaryUse}.</p>} />,
    },
    shippingSection(name),
  ];
}

/* ---------- PROTEIN (HGH-191AA) ---------- */
function proteinTemplate({ product, meta, name, sizes, skus }) {
  const fullName = meta?.fullName || name;
  const chemicalClass = meta?.chemicalClass || "Recombinant protein";
  const residues = meta?.residues;
  const mw = meta?.molecularWeight;
  const primaryUse = meta?.primaryUse || "Preclinical protein-signaling research";

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{fullName}</strong> is a{" "}
                <strong>{chemicalClass}</strong> supplied by ProSource Labs for
                laboratory research. Because it is a full-length recombinant protein
                (not a synthetic peptide fragment), it is handled and characterized
                differently than short synthetic peptide research compounds.
              </p>
              <p>
                Offered strictly for laboratory research applications — not intended
                for and not approved for human or veterinary consumption.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Molecular Characteristics & Vial Specifications`,
      body: (
        <>
          <p>
            Vial sizes: <strong>{sizes || "—"}</strong>. SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Chemical class:</strong> {chemicalClass}</li>
            {residues && <li><strong>Residue count:</strong> {residues} amino acids</li>}
            {mw && <li><strong>Molecular weight:</strong> {mw}</li>}
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Laboratory Handling & Storage`,
      body: (
        <>
          <StorageProtocol name={name} />
          <p>
            Because
            it is a large recombinant protein, {name} is <strong>especially
            sensitive to freeze-thaw cycling, mechanical agitation, and elevated
            temperature</strong>. Handle in a temperature-controlled environment
            and avoid vigorous vortexing during reconstitution.
          </p>
        </>
      ),
    },
    analyticalSection(name, "protein"),
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: <CustomApplication meta={meta} fallback={<p>{primaryUse}.</p>} />,
    },
    shippingSection(name),
  ];
}

/* ---------- BLEND (GLOW, KLOW) ---------- */
function blendTemplate({ product, meta, name, sizes, skus }) {
  const fullName = meta?.fullName || name;
  const primaryUse = meta?.primaryUse || "Preclinical multi-compound research applications";

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{fullName}</strong> is a <strong>lyophilized research blend</strong> —
                a multi-compound preparation combining more than one active research
                peptide into a single vial. Composition and compound ratios are
                printed on the batch card supplied with each vial; researchers
                should reference that card as the authoritative composition record.
              </p>
              <p>
                Offered strictly for laboratory research applications. Not intended
                for and not approved for human or veterinary consumption.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Vial Specifications`,
      body: (
        <>
          <p>
            Vial sizes: <strong>{sizes || "—"}</strong>. SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          {meta?.keyFacts && (
            <ul className="list-disc pl-6 space-y-1">
              {meta.keyFacts.map((f, i) => <KeyFactItem key={i} text={f} />)}
            </ul>
          )}
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Storage`,
      body: (
        <StorageProtocol name={name} />
      ),
    },
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: <CustomApplication meta={meta} fallback={<p>{primaryUse}.</p>} />,
    },
    shippingSection(name),
  ];
}

/* ---------- COSMETIC (Skincare — Blue Renewal) ---------- */
// Cosmetic product template. Deliberately renders skincare-appropriate
// sections (Ingredients INCI, Directions, Storage, Product Specs) and
// AVOIDS: research-use disclaimer, "laboratory research applications",
// therapeutic claims. Owner-specified positioning to keep the product
// distinct from the research peptide catalog.
function cosmeticTemplate({ product, meta, name, sizes, skus }) {
  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <p>
              <strong>{meta?.fullName || name}</strong> is a professional-
              strength multi-peptide facial serum formulated to help improve
              the appearance of smoother, firmer, and healthier-looking skin.
              Housed in a 30 mL frosted-glass airless pump bottle for precise
              daily application.
            </p>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Key Ingredients & Highlights`,
      body: (
        <>
          <p>
            <strong>Professional Strength:</strong> 2% GHK-Cu (Copper
            Tripeptide-1) and 3% SNAP-8 (Acetyl Octapeptide-3), supported by
            hyaluronic acid, niacinamide, panthenol, and beta-glucan.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>2% GHK-Cu:</strong> A well-known cosmetic peptide included in advanced skincare formulations to help support the appearance of firmer, healthier-looking skin.</li>
            <li><strong>3% SNAP-8:</strong> A cosmetic peptide used in premium anti-aging formulations to help soften the appearance of expression lines.</li>
            <li><strong>Hyaluronic Acid (Sodium Hyaluronate):</strong> Lightweight hydration to help skin appear plumper and more refreshed.</li>
            <li><strong>Niacinamide (Vitamin B3):</strong> Supports the appearance of brighter, more even-looking skin.</li>
            <li><strong>Panthenol (Pro-Vitamin B5):</strong> Conditions and moisturizes while supporting a healthy skin barrier.</li>
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
        </>
      ),
    },
    {
      icon: <Beaker size={16} />,
      title: `${name} — Directions`,
      body: (
        <p>
          {meta?.directions || `Apply 1–2 pumps to clean, dry skin on the face and neck morning and evening. Allow the serum to absorb before applying moisturizer. During daytime use, follow with a broad-spectrum sunscreen.`}
        </p>
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Full Ingredients (INCI)`,
      body: (
        <p className="text-sm leading-relaxed">
          {meta?.ingredients || `Aqua (Water), Glycerin, Propanediol, Copper Tripeptide-1 (GHK-Cu), Acetyl Octapeptide-3 (SNAP-8), Palmitoyl Tripeptide-1, Palmitoyl Tetrapeptide-7, Sodium Hyaluronate, Niacinamide, Panthenol, Allantoin, Beta-Glucan, Sodium PCA, Xanthan Gum, Phenoxyethanol, Ethylhexylglycerin.`}
        </p>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Storage`,
      body: (
        <p>
          {meta?.storage || `Store in a cool, dry place away from direct sunlight. Replace the protective cap after each use.`}
        </p>
      ),
    },
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Specifications`,
      body: (
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Product Name:</strong> {name}</li>
          <li><strong>Volume:</strong> {sizes || "30 mL / 1 fl oz"}</li>
          <li><strong>Bottle:</strong> Premium Frosted Glass Airless Pump</li>
          <li><strong>Formula:</strong> APD® Professional Formula</li>
          <li><strong>Manufacturer:</strong> ProSource Labs — Corona, California</li>
          <li><strong>SKU:</strong> <span className="font-mono-tech text-xs">{skus}</span></li>
        </ul>
      ),
    },
    shippingSection(name, { isCosmetic: true }),
  ];
}

/* ---------- NASAL SPRAY (ProSource Mist line) ---------- */
function nasalSprayTemplate({ product, meta, name, sizes, skus }) {
  const cat = product.category || "ProSource Mist";
  const primaryUse = meta?.primaryUse;
  const isBundle = /bundle/i.test(product.slug || "");

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{meta?.fullName || name}</strong> is part of the{" "}
                <strong>ProSource Mist</strong> nasal spray line — pre-mixed,
                ready-to-use research delivery in a sealed spray bottle. No
                reconstitution, no syringes, and no mixing steps. Just uncap,
                prime, and spray.
              </p>
              <p>
                Every {name} bottle is <strong>third-party tested for purity
                and potency</strong>, made in the USA, and shipped from
                ProSource Labs' Southern California facility. Supplied strictly
                for laboratory research applications — not intended for and
                not approved for human or veterinary consumption.
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Bottle Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in:{" "}
            <strong>{sizes || "—"}</strong>. Product SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Delivery format:</strong> Metered nasal spray bottle — ready to use</li>
            <li><strong>Volume:</strong> 5mL per bottle (approx. 50 metered sprays)</li>
            <li><strong>No prep required:</strong> No reconstitution, no syringes, no mixing</li>
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Handling & Storage`,
      body: (
        <>
          <p>
            Store {name} <strong>refrigerated (2–8°C / 36–46°F)</strong>,
            upright, and out of direct light. Bring to room temperature
            briefly before use for a smoother spray. Do not freeze.
          </p>
          <p>
            Once opened, keep the cap tight between uses. The metered pump is
            designed for repeat dosing throughout the day, which makes the
            spray format especially convenient for research protocols that
            call for multiple administrations across a 24-hour window — no
            new needle or vial required each time.
          </p>
        </>
      ),
    },
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: (
        <CustomApplication
          meta={meta}
          fallback={
            primaryUse ? <p>{primaryUse}.</p> : (
              <p>
                {name} is used in preclinical and cell-culture research within
                the {cat} category. Consult peer-reviewed literature relevant
                to your specific experimental question for detailed
                methodology.{isBundle ? " Bundle purchasers receive all four bottles in the ProSource Mist line for stacked-protocol convenience." : ""}
              </p>
            )
          }
        />
      ),
    },
    shippingSection(name),
  ];
}

/* ---------- PEPTIDE (default) ---------- */
function peptideTemplate({ product, meta, name, sizes, skus }) {
  const cat = product.category || "research peptide";
  const chemicalClass = meta?.chemicalClass;
  const residues = meta?.residues;
  const mw = meta?.molecularWeight;
  const sequence = meta?.sequence;
  const primaryUse = meta?.primaryUse;

  return [
    {
      icon: <FlaskConical size={16} />,
      title: `${name} — Product Overview`,
      body: (
        <CustomOverview
          meta={meta}
          fallback={
            <>
              <p>
                <strong>{meta?.fullName || name}</strong>{" "}
                {chemicalClass ? (
                  <>is a <strong>{chemicalClass}</strong> supplied by ProSource Labs, </>
                ) : (
                  <>is a research-grade synthetic peptide supplied by ProSource Labs, </>
                )}
                a trusted US-based direct-to-researcher retail store in the {cat} category. All
                lab-verified {name} vials are synthesized in a controlled laboratory
                environment, <strong>lyophilized (freeze-dried)</strong> for long-term
                stability, and sealed in tamper-evident vials before undergoing
                third-party analytical testing.
              </p>
              <p>
                {name} is offered <strong>strictly for laboratory research
                applications</strong> — including in vitro assays, cell-culture
                experiments, receptor-binding characterization studies, biochemical
                pathway investigation, and preclinical mechanism-of-action work in
                animal models. Not intended for and not approved for human or
                veterinary consumption. Every {name} order ships with a batch
                identifier for full traceability back to its manufacturing lot,
                HPLC purity report, and Certificate of Analysis (COA).
              </p>
            </>
          }
        />
      ),
    },
    {
      icon: <Microscope size={16} />,
      title: `${name} — Molecular Characteristics & Vial Specifications`,
      body: (
        <>
          <p>
            ProSource Labs supplies {name} in the following available vial sizes:{" "}
            <strong>{sizes || "—"}</strong>. Each {name} vial contains{" "}
            <strong>pure lyophilized peptide</strong> — no fillers, no bulking
            agents. Product SKU references:{" "}
            <span className="font-mono text-xs">{skus || "—"}</span>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            {chemicalClass && <li><strong>Peptide class:</strong> {chemicalClass}</li>}
            {residues && <li><strong>Residue count:</strong> {residues} amino acids</li>}
            {mw && <li><strong>Molecular weight:</strong> {mw}</li>}
            {sequence && <li><strong>Sequence:</strong> <span className="font-mono text-xs">{sequence}</span></li>}
            {meta?.keyFacts?.map((f, i) => <KeyFactItem key={`kf-${i}`} text={f} />)}
          </ul>
          <p>
            Batches are characterized by <strong>ESI mass spectrometry</strong>{" "}
            to confirm molecular weight against the theoretical value, with
            amino-acid sequence confirmation on selected QA lots. Peptide content
            assay distinguishes net peptide mass from residual counter-ion salt.
          </p>
        </>
      ),
    },
    {
      icon: <Snowflake size={16} />,
      title: `${name} — Laboratory Handling & Storage`,
      body: (
        <>
          <StorageProtocol name={name} />
          <p>
            All laboratory handling of {name} should follow the receiving
            institution's SOPs and applicable peer-reviewed protocols relevant
            to the researcher's specific experimental question. ProSource Labs
            does not publish end-user preparation or usage instructions.
          </p>
        </>
      ),
    },
    analyticalSection(name, "peptide"),
    qaSection(name),
    {
      icon: <Beaker size={16} />,
      title: `${name} — Typical Research Application`,
      body: (
        <CustomApplication
          meta={meta}
          fallback={
            primaryUse ? <p>{primaryUse}.</p> : (
              <p>
                {name} is used in preclinical and cell-culture research within the{" "}
                {cat} category. Consult peer-reviewed literature relevant to your
                specific experimental question for detailed methodology.
              </p>
            )
          }
        />
      ),
    },
    shippingSection(name),
  ];
}

/* ---------- Reusable sub-sections ---------- */
function analyticalSection(name, kind) {
  const method =
    kind === "peptide"
      ? "reverse-phase High Performance Liquid Chromatography (HPLC)"
      : kind === "protein"
      ? "SDS-PAGE and reverse-phase HPLC"
      : kind === "coenzyme"
      ? "HPLC purity analysis"
      : "HPLC purity analysis";
  return {
    icon: <ShieldCheck size={16} />,
    title: `${name} — Third-Party Analytical Testing`,
    body: (
      <>
        <p>
          Every batch of {name} released by ProSource Labs undergoes independent{" "}
          <strong>{method}</strong> — the industry-standard analytical method
          for this class of research compound. Target chromatographic purity is
          typically <strong>≥ 99%</strong>.
        </p>
        <p>
          Additional characterization may include ESI mass spectrometry for
          molecular weight confirmation, Karl Fischer titration for residual
          moisture, and appearance/solubility inspection. A batch-specific
          Certificate of Analysis (COA) is available on request through
          customer support after purchase.
        </p>
      </>
    ),
  };
}

function qaSection(name) {
  return {
    icon: <ShieldCheck size={16} />,
    title: `${name} — Quality Assurance Process`,
    body: (
      <>
        <p>
          Each production lot of {name} is quarantined pending analytical
          release, tested against documented <strong>purity, identity, and
          moisture acceptance criteria</strong>, and only released to sellable
          inventory after passing every QA checkpoint. Batches that fail any
          single criterion are quarantined and never reach customer-facing
          inventory.
        </p>
        <p>
          If a vial of {name} you received from ProSource Labs ever fails your
          own institution's incoming QA testing, contact customer support at
          support@prosourcelabs.org — we retain samples of every batch for
          cross-verification.
        </p>
      </>
    ),
  };
}

function shippingSection(name, opts = {}) {
  const isCosmetic = opts.isCosmetic === true;
  const itemNoun = isCosmetic ? "bottles" : "vials";
  const privacyNoun = isCosmetic ? "customer privacy" : "researcher privacy";
  return {
    icon: <Package size={16} />,
    title: `${name} — Packaging & Fast Southern California Shipping`,
    body: (
      <>
        <p>
          {name} {itemNoun} ship in <strong>tamper-evident, moisture-resistant,
          cold-chain-friendly outer packaging</strong> from our Southern
          California distribution facility for fast US domestic delivery —
          typically 2-4 business days coast-to-coast, with expedited options
          available at checkout.
        </p>
        <p>
          Every {name} shipment is <strong>discreet</strong> — the outer
          packaging carries no product-specific branding or direct reference to
          the {isCosmetic ? "product" : "compound"} name inside, protecting {privacyNoun}. Tracking
          numbers are emailed automatically.{isCosmetic ? "" : " For institutional or high-volume orders, dedicated wholesale pricing is available through the wholesale portal."}
        </p>
      </>
    ),
  };
}
